
import React, { useState, useEffect, useMemo } from 'react';
import { Play, Square, Loader2, History, Clock, ChevronRight, ChevronLeft, CalendarDays, Inbox, Coffee, ShieldAlert } from 'lucide-react';
import Dialog from '../components/ui/Dialog';
import { api, Store, Staff, Attendance, getJSTDateString, calculateDurationMinutes, getTrialStatus } from '../api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';

const ClockDisplay = () => {
    const [time, setTime] = useState(new Date());
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);
    return (
        <div className="mt-8 mb-12 text-center">
            <div className="text-slate-500 font-bold text-lg mb-2">
                {time.getFullYear()}年{time.getMonth()+1}月{time.getDate()}日（{['日','月','火','水','木','金','土'][time.getDay()]}）
            </div>
            <div className="text-6xl font-mono font-bold text-slate-800 tracking-wider">
                {String(time.getHours()).padStart(2,'0')}:{String(time.getMinutes()).padStart(2,'0')}
            </div>
        </div>
    );
};

const UserAttendancePage: React.FC = () => {
  const { showToast } = useToast();
  const { currentUser: authUser } = useAuth();
  const { userProfile: contextUserProfile } = useUser();
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null);
  const [staffProfile, setStaffProfile] = useState<Staff | null>(null);
  const [targetStore, setTargetStore] = useState<Store | null>(null);
  const [currentStatus, setCurrentStatus] = useState<'none' | 'working' | 'break' | 'paid_leave'>('none');
  const [activeRecord, setActiveRecord] = useState<Attendance | null>(null);
  const [activeTab, setActiveTab] = useState<'clock' | 'history'>('clock');
  const [monthlyRecords, setMonthlyRecords] = useState<Attendance[]>([]);
  const [targetDate, setTargetDate] = useState(new Date());
  const [locationStatus, setLocationStatus] = useState<'checking' | 'allowed' | 'denied' | 'error'>('checking');
  const [isLoading, setIsLoading] = useState(true);
  const [dialog, setDialog] = useState<{ isOpen: boolean, title: string, message: string, type: 'info' | 'confirm' | 'alert' | 'prompt', onConfirm: () => void }>({ isOpen: false, title: '', message: '', type: 'info', onConfirm: () => {} });

  const targetMonthStr = useMemo(() => {
      const y = targetDate.getFullYear();
      const m = String(targetDate.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
  }, [targetDate]);

  // 勤怠機能が有効かどうかの判定（ビジネスプランまたはトライアル中）
  const isAttendanceEnabled = useMemo(() => {
    if (!targetStore) return true; // 読み込み中は一旦許可
    const trial = getTrialStatus(targetStore);
    const isTrialActive = !targetStore.stripeSubscriptionId && !trial.isExpired;
    return targetStore.plan === 'business' || isTrialActive;
  }, [targetStore]);

  const loadData = async () => {
      setIsLoading(true);
      // AuthContext/UserContextからユーザーIDを取得
      const userId = authUser?.uid || contextUserProfile?.id || null;
      if (userId) {
          const user = { id: userId };
          setCurrentUser(user);
          try {
              const [stores, allStaff] = await Promise.all([api.getStores(), api.getStaff()]);
              const profile = allStaff.find(s => s.id === user.id);
              setStaffProfile(profile || null);
              if (stores.length > 0) {
                  const store = stores[0];
                  setTargetStore(store);
                  
                  // プラン制限に引っかからない場合のみ位置情報とデータを取得
                  const trial = getTrialStatus(store);
                  const isTrialActive = !store.stripeSubscriptionId && !trial.isExpired;
                  if (store.plan === 'business' || isTrialActive) {
                      checkLocation(store);
                      const unfinished = await api.getUnfinishedAttendance(user.id);
                      const today = getJSTDateString();
                      const allAttendance = await api.getAttendance(store.id);
                      const todayRecords = allAttendance.filter(a => a.staffId === user.id && a.date === today);
                      setMonthlyRecords(allAttendance.filter(a => a.staffId === user.id && a.date.startsWith(targetMonthStr) && (a.status === 'finished' || a.status === 'paid_leave')));
                      if (unfinished) { setActiveRecord(unfinished); setCurrentStatus(unfinished.status as 'none' | 'working' | 'break' | 'paid_leave'); }
                      else if (todayRecords.some(r => r.status === 'paid_leave')) { setCurrentStatus('paid_leave'); }
                      else { setActiveRecord(null); setCurrentStatus('none'); }
                  }
              }
          } catch (e) {
              console.error(e);
          } finally {
              setIsLoading(false);
          }
      }
  };

  useEffect(() => { loadData(); }, [targetMonthStr]);

  const checkLocation = (store: Store) => {
      if (!store.location?.lat || !store.location?.lng) { 
          setLocationStatus('allowed'); 
          return; 
      }
      if (!navigator.geolocation) { 
          setLocationStatus('error'); 
          showToast('お使いのブラウザは位置情報をサポートしていません。', 'error');
          return; 
      }
      
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 15000, 
        maximumAge: 0
      };

      setLocationStatus('checking');

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const R = 6371; 
          const dLat = (store.location!.lat - pos.coords.latitude) * (Math.PI / 180); 
          const dLon = (store.location!.lng - pos.coords.longitude) * (Math.PI / 180);
          const a = 
            Math.sin(dLat / 2) * Math.sin(dLat / 2) + 
            Math.cos(pos.coords.latitude * (Math.PI / 180)) * 
            Math.cos(store.location!.lat * (Math.PI / 180)) * 
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const distance = Math.round(R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))) * 1000);
          
          if (distance <= 300) {
              setLocationStatus('allowed');
          } else {
              setLocationStatus('denied');
          }
        }, 
        (err) => {
          setLocationStatus('error');
          let msg = '位置情報の取得に失敗しました。';
          if (err.code === err.PERMISSION_DENIED) {
              msg = '位置情報の利用を許可してください。';
          }
          showToast(msg, 'error');
        }, 
        options
      );
  };

  const handleClockIn = async () => {
      if (locationStatus !== 'allowed') {
          showToast('勤務場所に着いてから打刻してください。', 'error');
          return;
      }
      setIsLoading(true);
      const timeStr = `${String(new Date().getHours()).padStart(2,'0')}:${String(new Date().getMinutes()).padStart(2,'0')}`;
      if (!currentUser) return;
      const newRecord: Attendance = { id: `att_${Date.now()}`, staffId: currentUser.id, storeId: targetStore?.id || '', date: getJSTDateString(), startTime: timeStr, breakTime: 0, status: 'working' };
      try { await api.saveAttendance(newRecord); await loadData(); setDialog({ isOpen: true, title: '出勤完了', message: `${timeStr} に出勤しました。`, type: 'info', onConfirm: () => setDialog(d => ({...d, isOpen: false})) }); } catch (e) { console.error(e); } finally { setIsLoading(false); }
  };

  const handleClockOut = async () => {
      if (!activeRecord) return;
      setIsLoading(true);
      const timeStr = `${String(new Date().getHours()).padStart(2,'0')}:${String(new Date().getMinutes()).padStart(2,'0')}`;
      try { await api.saveAttendance({ ...activeRecord, endTime: timeStr, status: 'finished' }); await loadData(); setDialog({ isOpen: true, title: '退勤完了', message: `${timeStr} に退勤しました。お疲れ様でした！`, type: 'info', onConfirm: () => setDialog(d => ({...d, isOpen: false})) }); } catch (e) { console.error(e); } finally { setIsLoading(false); }
  };

  const handleBreakStart = async () => {
      if (!activeRecord) return;
      setIsLoading(true);
      const timeStr = `${String(new Date().getHours()).padStart(2,'0')}:${String(new Date().getMinutes()).padStart(2,'0')}`;
      try {
          await api.saveAttendance({ ...activeRecord, status: 'break', breakStartTime: timeStr });
          await loadData();
          setDialog({ isOpen: true, title: '休憩開始', message: `${timeStr} に休憩を開始しました。`, type: 'info', onConfirm: () => setDialog(d => ({...d, isOpen: false})) });
      } catch (e) { console.error(e); } finally { setIsLoading(false); }
  };

  const handleBreakEnd = async () => {
      if (!activeRecord) return;
      setIsLoading(true);
      const timeStr = `${String(new Date().getHours()).padStart(2,'0')}:${String(new Date().getMinutes()).padStart(2,'0')}`;
      try {
          await api.endBreak(activeRecord, timeStr);
          await loadData();
          showToast('休憩終了。');
      } catch (e) { console.error(e); } finally { setIsLoading(false); }
  };

  const changeMonth = (delta: number) => {
      const nextDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + delta, 1);
      setTargetDate(nextDate);
  };

  const monthlyStats = useMemo(() => {
      let totalMin = 0; monthlyRecords.forEach(r => { if(r.status === 'finished') totalMin += calculateDurationMinutes(r.startTime || '', r.endTime || '', r.breakTime); });
      const wage = staffProfile?.hourlyWage || 0;
      return { 
          totalHours: (totalMin / 60).toFixed(1), 
          estimatedSalary: staffProfile?.employmentType === 'full_time' ? '-' : Math.round((totalMin / 60) * wage).toLocaleString() 
      };
  }, [monthlyRecords, staffProfile]);

  // プラン制限表示
  if (!isLoading && !isAttendanceEnabled) {
    return (
        <div className="min-h-[80vh] flex flex-col items-center justify-center p-8 text-center animate-in">
            <div className="w-20 h-20 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mb-6 border border-orange-100">
                <ShieldAlert size={40} />
            </div>
            <h2 className="text-xl font-black text-slate-800 mb-2">機能制限中</h2>
            <p className="text-slate-500 text-sm font-bold leading-relaxed mb-8">
                打刻機能は現在ご利用いただけません。<br/>
                店舗のプラン設定をご確認ください。
            </p>
        </div>
    );
  }

  return (
    <div className="animate-in pb-20 bg-[#F8FAFC]">
      <Dialog isOpen={dialog.isOpen} type={dialog.type} title={dialog.title} message={dialog.message} onConfirm={dialog.onConfirm} onCancel={() => setDialog(d => ({...d, isOpen: false}))} />
      
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="flex p-1.5 max-w-sm mx-auto">
              <button 
                  onClick={() => setActiveTab('clock')} 
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${activeTab === 'clock' ? 'bg-blue-600 shadow-lg shadow-blue-100 text-white' : 'text-slate-400 hover:text-slate-600'}`}
              >
                  <Clock size={16} strokeWidth={2.5} /> 打刻
              </button>
              <button 
                  onClick={() => setActiveTab('history')} 
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${activeTab === 'history' ? 'bg-blue-600 shadow-lg shadow-blue-100 text-white' : 'text-slate-400 hover:text-slate-600'}`}
              >
                  <History size={16} strokeWidth={2.5} /> 履歴
              </button>
          </div>
      </div>

      {isLoading ? (
          <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
              <Loader2 className="animate-spin text-blue-600" size={32} />
              <p className="text-slate-400 text-xs font-bold">読み込み中...</p>
          </div>
      ) : activeTab === 'clock' ? (
          <div className="p-6 flex flex-col items-center min-h-[70vh]">
              <ClockDisplay />
              {activeRecord?.startTime && currentStatus === 'working' && (
                  <div className="mb-8 -mt-8 flex flex-col items-center gap-1">
                      <div className="inline-block bg-blue-50 text-blue-700 px-5 py-2 rounded-full text-sm font-black border border-blue-100 shadow-sm">
                          勤務中：{activeRecord.startTime}〜
                      </div>
                  </div>
              )}
              {currentStatus === 'paid_leave' && (
                  <div className="mb-8 -mt-8 animate-in">
                      <div className="inline-block bg-emerald-50 text-emerald-700 px-5 py-2 rounded-full text-sm font-black border border-emerald-100">
                          本日は有給休暇取得済みです
                      </div>
                  </div>
              )}
              
              <div className={`w-full max-w-sm mb-10 p-4 rounded-2xl border text-xs font-black flex items-center justify-center text-center leading-relaxed shadow-sm transition-colors ${locationStatus === 'allowed' ? 'bg-white border-emerald-200 text-emerald-600' : 'bg-orange-50 border-orange-200 text-orange-600'}`}>
                  {locationStatus === 'checking' && (
                      <div className="flex items-center gap-2">
                          <Loader2 className="animate-spin" size={14} />
                          <span>位置情報を確認しています...</span>
                      </div>
                  )}
                  {locationStatus === 'allowed' && '打刻エリア内にいます'}
                  {(locationStatus === 'denied' || locationStatus === 'error') && '勤務場所に着いてから打刻してください。'}
              </div>
              
              {currentStatus === 'none' && (
                  <div className="flex flex-col items-center gap-6">
                      <button 
                          onClick={handleClockIn} 
                          disabled={locationStatus !== 'allowed' || isLoading} 
                          className={`w-56 h-56 rounded-full flex flex-col items-center justify-center shadow-2xl transition-all border-[10px] active:scale-90 ${locationStatus === 'allowed' ? 'bg-blue-600 border-blue-50 text-white shadow-blue-200' : 'bg-slate-200 border-white text-slate-400 cursor-not-allowed'}`}
                      >
                          {isLoading ? <Loader2 className="animate-spin mb-2" size={40} /> : <Play size={44} className="mb-2" fill="currentColor" />}
                          <span className="text-2xl font-black tracking-widest">出勤</span>
                      </button>
                      
                      {locationStatus !== 'allowed' && (
                          <button 
                            onClick={() => targetStore && checkLocation(targetStore)}
                            className="text-xs font-bold text-blue-600 underline"
                          >
                            位置情報を再取得する
                          </button>
                      )}
                  </div>
              )}
              {currentStatus === 'working' && (
                  <div className="flex flex-col items-center gap-6">
                      {/* 退勤：メインアクション（出勤ボタンと同じ大きな丸ボタン） */}
                      <button
                          onClick={handleClockOut}
                          disabled={isLoading}
                          className="w-56 h-56 rounded-full bg-slate-800 border-[10px] border-slate-50 text-white flex flex-col items-center justify-center shadow-2xl shadow-slate-200 active:scale-90 transition-all"
                      >
                          {isLoading ? <Loader2 className="animate-spin mb-2" size={40} /> : <Square size={44} className="mb-2" fill="currentColor" />}
                          <span className="text-2xl font-black tracking-widest">退勤</span>
                      </button>
                      {/* 休憩：サブアクション */}
                      <button
                          onClick={handleBreakStart}
                          disabled={isLoading}
                          className="flex items-center gap-2 text-amber-600 font-black text-sm bg-amber-50 border border-amber-200 px-8 py-3 rounded-full active:scale-95 transition-all"
                      >
                          <Coffee size={16} />休憩を取る
                      </button>
                  </div>
              )}
              {currentStatus === 'break' && (
                  <div className="flex flex-col items-center gap-6">
                      {/* 休憩中ステータス表示（丸いインジケーター） */}
                      <div className="w-56 h-56 rounded-full bg-amber-50 border-[10px] border-amber-100 flex flex-col items-center justify-center shadow-inner">
                          <Coffee size={44} className="text-amber-400 mb-2" />
                          <span className="text-2xl font-black text-amber-600 tracking-widest">休憩中</span>
                          <span className="text-xs text-amber-400 font-bold mt-1">{activeRecord?.breakStartTime}〜</span>
                      </div>
                      {/* 休憩終了ボタン */}
                      <button
                          onClick={handleBreakEnd}
                          disabled={isLoading}
                          className="flex items-center gap-2 text-white font-black text-sm bg-amber-500 px-10 py-4 rounded-full shadow-xl shadow-amber-100 active:scale-95 transition-all"
                      >
                          <Play size={16} fill="currentColor" />休憩終了
                      </button>
                  </div>
              )}
          </div>
      ) : (
          <div className="p-4 space-y-4">
              <div className="bg-white border border-slate-200 p-4 rounded-2xl shadow-sm">
                  <div className="flex items-center justify-between mb-4 bg-slate-50 rounded-xl p-1 px-2">
                      <button onClick={() => changeMonth(-1)} className="p-1.5 hover:bg-white rounded-lg text-slate-400 active:scale-90 transition-all border border-transparent hover:border-slate-200">
                          <ChevronLeft size={20} strokeWidth={3} />
                      </button>
                      <h3 className="text-sm font-black text-slate-700 tracking-tight">{targetDate.getFullYear()}年 {targetDate.getMonth()+1}月</h3>
                      <button onClick={() => changeMonth(1)} className="p-1.5 hover:bg-white rounded-lg text-slate-400 active:scale-90 transition-all border border-transparent hover:border-slate-200">
                          <ChevronRight size={20} strokeWidth={3} />
                      </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 py-2">
                      <div className="flex flex-col items-center">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">月間実労働</p>
                          <p className="text-xl font-black text-slate-800 leading-none">{monthlyStats.totalHours}<span className="text-[10px] ml-0.5 opacity-50">h</span></p>
                      </div>
                      <div className="flex flex-col items-center border-l border-slate-100">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">概算給与</p>
                          <p className="text-xl font-black text-slate-800 leading-none">{monthlyStats.estimatedSalary}<span className="text-[10px] ml-0.5 opacity-50">円</span></p>
                      </div>
                  </div>
              </div>

              <div className="space-y-3">
                  <div className="flex items-center justify-between px-2 mb-1">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">勤務履歴</h4>
                      <span className="text-[10px] font-bold text-slate-300">{monthlyRecords.length} 件の記録</span>
                  </div>
                  
                  {monthlyRecords.length === 0 ? (
                      <div className="py-16 flex flex-col items-center justify-center gap-4 bg-white rounded-3xl border border-slate-100 shadow-sm border-dashed">
                          <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-slate-200">
                              <Inbox size={24} />
                          </div>
                          <p className="text-sm font-black text-slate-300 tracking-wider">勤務記録がありません</p>
                      </div>
                  ) : monthlyRecords.map(record => (
                      <div key={record.id} className="bg-white border border-slate-100 rounded-2xl p-3.5 shadow-sm flex items-center justify-between active:scale-[0.98] transition-all">
                          <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center font-black ${record.status === 'paid_leave' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                                  <span className="text-[7px] uppercase">{record.date.split('-')[1]}月</span>
                                  <span className="text-base leading-none">{record.date.split('-')[2]}</span>
                              </div>
                              <div>
                                  <div className="text-sm font-black text-slate-800">
                                      {record.status === 'paid_leave' ? '有給休暇取得' : `${record.startTime} 〜 ${record.endTime}`}
                                  </div>
                                  <div className="text-[10px] text-slate-400 font-bold mt-0.5">
                                      {record.status === 'paid_leave' ? '全日休暇' : `実働：${(calculateDurationMinutes(record.startTime!, record.endTime!, record.breakTime)/60).toFixed(1)}時間／休憩：${record.breakTime}分`}
                                  </div>
                              </div>
                          </div>
                          <div className="bg-slate-50 p-1 rounded-full">
                              <ChevronRight size={14} className="text-slate-300" strokeWidth={3} />
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      )}
    </div>
  );
};

export default UserAttendancePage;
